import { ArpMediaType, InstrumentCategory } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import usePrevious from '../../../hooks/usePrevious'
import { useArpMedias } from '../../../queries/arpMedia'
import { useTreeState } from '../../../stores/Root'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Flex } from '../../core/Flex'
import Text from '../../core/Text/Text'
import {
  ScrollArea,
  ScrollAreaCorner,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport
} from './Scroll'
import StemRow from './StemRow'
import * as StemTable from './StemTable'

const useDiscoverContent = () => {
  const [selectedCategory, setSelectedCategory] = useState<InstrumentCategory>(
    InstrumentCategory.DRUMS
  )
  const { alternatePlayer } = useTreeState()
  const previousCategory = usePrevious(selectedCategory)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useArpMedias({
    perPage: 50,
    parts: ['subComponents'],
    shadowBlocklist: true,
    type: [ArpMediaType.STEM],
    instrumentCategory: selectedCategory
  })

  useEffect(() => {
    if (ArpRegistry.readyForStudioUse && !alternatePlayer.loading) {
      alternatePlayer.initialize(false)
    }
  }, [ArpRegistry.readyForStudioUse])

  useEffect(() => {
    handleCategoryChange()
  }, [])

  useEffect(() => {
    if (previousCategory !== selectedCategory) {
      handleCategoryChange()
    }
  }, [previousCategory, selectedCategory])

  useEffect(() => {
    return () => {
      alternatePlayer.stop()
      alternatePlayer.userSetPlayhead(0)
      alternatePlayer.clearSong()
    }
  })

  const handleCategoryChange = async () => {}

  const stems: IArpMedia[] = useMemo(() => {
    if (!data?.pages) return []
    const unprocessedArps = data.pages.flatMap((page) => page.data)
    return ArpRegistry.setMedias(unprocessedArps)
  }, [data])

  return {
    handleFetchMore: fetchNextPage,
    alternatePlayer,
    selectedCategory,
    setSelectedCategory,
    stems,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage
  }
}

const DiscoverContent = () => {
  const {
    handleFetchMore,
    alternatePlayer,
    selectedCategory,
    setSelectedCategory,
    stems,
    hasMore,
    isLoading
  } = useDiscoverContent()

  return (
    <Flex css={{ height: '100%', position: 'relative' }}>
      <StemTable.SideNavigation
        handleClick={(key) => setSelectedCategory(key)}
        selected={selectedCategory}
      />
      <StemTable.TableWrapper css={{ padding: '0 $16' }}>
        <StemTable.Header />
        <ScrollArea>
          <ScrollAreaViewport id="infinite-scroll-container">
            {isLoading ? (
              <CenteredLoadingAnimation />
            ) : (
              <InfiniteScroll
                dataLength={stems.length}
                next={handleFetchMore}
                hasMore={hasMore}
                scrollableTarget="infinite-scroll-container"
                loader={<CenteredLoadingAnimation />}
                style={{ overflow: 'visible' }}
              >
                {!alternatePlayer.loading &&
                  (stems.length ? (
                    stems.map(
                      (stem, i) =>
                        stem &&
                        stem.getStemStore() && (
                          <StemRow stem={stem} index={i} key={`${stem.id}=${stem.type}`} />
                        )
                    )
                  ) : (
                    <Text type="secondary" size="14">
                      No results
                    </Text>
                  ))}
              </InfiniteScroll>
            )}
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical">
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
          <ScrollAreaCorner />
        </ScrollArea>
      </StemTable.TableWrapper>
    </Flex>
  )
}

export default observer(DiscoverContent)
